/**
 * Intelligence page (M9) — CEO-grade intelligence layer.
 *
 * Tabs: Advisor · Spend · Brand · Campaigns · Opportunities · Reports ·
 * Workforce · Budget · Competitive. Every surface answers at least one of:
 * what happened / why / what should we do next / risk / opportunity.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bot, CheckCircle2, ChevronRight, Coins,
  Compass, FileText, Flame, Gauge, LineChart, Megaphone,
  MessageSquareText, PauseCircle, ShieldCheck, Sparkles,
  Target, TrendingDown, TrendingUp, Wallet, Wand2,
} from "lucide-react";
import { toast } from "sonner";

import { useSurfacePolicy } from "@/entities/auth/useSurfacePolicy";
import { useCurrentWorkspace } from "@/entities/workspace";
import {
  BUDGET_STATUS_TONES, PRIORITY_TONES, SEVERITY_TONES,
  acceptOpportunity, approvePerformanceObservation, generateReport, getBrand, getBriefing, getBudget, getCustomerOneAudit, getPerformanceWorkflowTrace,
  getPerformanceSnapshot, getSpendRollup, getStalledCampaigns, getWorkforce, listCompetitiveProviders,
  listOpportunities, listPerformanceObservations, listRecommendations, listReports, putBudget,
  recommendationAction, rejectOpportunity, rejectPerformanceObservation, rescoreBrand, syncZernioPerformance,
  type BudgetThreshold, type CustomerOneAudit, type DecisionRecommendation, type ExecReport,
  type Opportunity, type PerformanceObservation, type ReportCadence, type WorkflowTrace,
} from "@/entities/intelligence";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Skeleton } from "@/shared/ui/skeleton";
import { Switch } from "@/shared/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { HorizontalScrollCue } from "@/shared/ui/horizontal-scroll-cue";

const cur = (v: number) => `$${v.toFixed(v < 1 ? 4 : 2)}`;

export default function IntelligencePage(): JSX.Element {
  const workspace = useCurrentWorkspace();
  const { canViewInternalDiagnostics } = useSurfacePolicy();
  if (!workspace) {
    return (
      <div data-testid="intel-empty" className="mx-auto max-w-2xl px-6 py-16 text-center">
        <h1 className="font-display text-3xl font-semibold">Intelligence</h1>
        <p className="mt-2 text-sm text-muted-foreground">Select a workspace to see intelligence.</p>
      </div>
    );
  }
  return (
    <div data-testid="intel-page" className="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6 sm:py-8">
      <header className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-background/70 px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            <Sparkles className="h-3 w-3" /> Intelligence · CEO-grade
          </div>
          <h1 data-testid="intel-title" className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-5xl">
            Intelligence
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            What happened, why, and what to do next — for every surface of the marketing operating system.
          </p>
        </div>
      </header>

      <Tabs defaultValue="advisor" className="space-y-4">
        <HorizontalScrollCue testId="intel-tabs-scroll">
        <TabsList data-testid="intel-tabs" className="flex h-auto w-max min-w-full justify-start gap-1 whitespace-nowrap px-1">
          <TabsTrigger data-testid="tab-advisor" value="advisor">Advisor</TabsTrigger>
          <TabsTrigger data-testid="tab-opportunities" value="opportunities">Opportunities</TabsTrigger>
          <TabsTrigger data-testid="tab-performance" value="performance">Performance</TabsTrigger>
          {canViewInternalDiagnostics ? <TabsTrigger data-testid="tab-readiness" value="readiness">Readiness</TabsTrigger> : null}
          {canViewInternalDiagnostics ? <TabsTrigger data-testid="tab-spend" value="spend">Spend</TabsTrigger> : null}
          <TabsTrigger data-testid="tab-brand" value="brand">Brand</TabsTrigger>
          <TabsTrigger data-testid="tab-campaigns" value="campaigns">Campaigns</TabsTrigger>
          <TabsTrigger data-testid="tab-reports" value="reports">Reports</TabsTrigger>
          <TabsTrigger data-testid="tab-workforce" value="workforce">Workforce</TabsTrigger>
          {canViewInternalDiagnostics ? <TabsTrigger data-testid="tab-budget" value="budget">Budget</TabsTrigger> : null}
          <TabsTrigger data-testid="tab-competitive" value="competitive">Competitive</TabsTrigger>
        </TabsList>
        </HorizontalScrollCue>

        <TabsContent value="advisor" className="space-y-4"><AdvisorPanel ws={workspace.id} /></TabsContent>
        <TabsContent value="opportunities" className="space-y-4"><OpportunitiesPanel ws={workspace.id} /></TabsContent>
        <TabsContent value="performance" className="space-y-4"><PerformancePanel ws={workspace.id} /></TabsContent>
        {canViewInternalDiagnostics ? <TabsContent value="readiness" className="space-y-4"><CustomerOneReadinessPanel ws={workspace.id} /></TabsContent> : null}
        {canViewInternalDiagnostics ? <TabsContent value="spend" className="space-y-4"><SpendPanel ws={workspace.id} /></TabsContent> : null}
        <TabsContent value="brand" className="space-y-4"><BrandPanel ws={workspace.id} /></TabsContent>
        <TabsContent value="campaigns" className="space-y-4"><CampaignsPanel ws={workspace.id} /></TabsContent>
        <TabsContent value="reports" className="space-y-4"><ReportsPanel ws={workspace.id} /></TabsContent>
        <TabsContent value="workforce" className="space-y-4"><WorkforcePanel ws={workspace.id} /></TabsContent>
        {canViewInternalDiagnostics ? <TabsContent value="budget" className="space-y-4"><BudgetPanel ws={workspace.id} /></TabsContent> : null}
        <TabsContent value="competitive" className="space-y-4"><CompetitivePanel ws={workspace.id} /></TabsContent>
      </Tabs>
    </div>
  );
}

function CustomerOneReadinessPanel({ ws }: { ws: string }): JSX.Element {
  const q = useQuery({ queryKey: ["intel", "customer-one-audit", ws], queryFn: () => getCustomerOneAudit(ws), refetchInterval: 30_000 });
  if (q.isLoading) return <Skeleton className="h-80 w-full" />;
  if (!q.data) return <Card><CardContent className="p-6 text-sm text-muted-foreground">Customer-One audit is unavailable.</CardContent></Card>;
  return <CustomerOneAuditView audit={q.data} />;
}

const AUDIT_TONE: Record<string, string> = {
  passed: "border-emerald-500/30 bg-emerald-500/5",
  action_required: "border-rose-500/40 bg-rose-500/5",
  deferred_by_founder: "border-sky-500/30 bg-sky-500/5",
  manual_verification: "border-amber-500/30 bg-amber-500/5",
};

function CustomerOneAuditView({ audit }: { audit: CustomerOneAudit }): JSX.Element {
  return <Card data-testid="customer-one-audit" className="border-border/60 bg-card/70">
    <CardHeader>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><CardTitle>Customer-One reliability</CardTitle><CardDescription>Canonical product truth. Founder-deferred external work is separated from real blockers.</CardDescription></div>
        <Badge variant="outline" className={audit.core_ready ? "text-emerald-600" : "text-rose-600"}>{audit.core_ready ? "Core ready" : `${audit.blocking_count} blocker(s)`}</Badge>
      </div>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {audit.checks.map((check) => <div key={check.key} data-testid={`customer-one-${check.key}`} className={`rounded-lg border p-3 ${AUDIT_TONE[check.status] ?? ""}`}>
          <div className="flex items-start justify-between gap-2"><span className="font-semibold">{check.label}</span><Badge variant="outline" className="shrink-0 text-[10px]">{check.status.replaceAll("_", " ")}</Badge></div>
          <p className="mt-1 text-xs text-muted-foreground">{check.detail}</p>
          {check.action_path && <a href={check.action_path} className="mt-2 inline-block text-xs font-semibold text-primary hover:underline">Open →</a>}
        </div>)}
      </div>
      <div className="rounded-lg border border-border/60 p-3 text-sm"><span className="font-semibold">Next:</span> {audit.next_action}</div>
      <p className="text-xs text-muted-foreground">{audit.policy}</p>
      <section data-testid="customer-one-walkthrough" className="space-y-3 border-t border-border/60 pt-5">
        <div><h3 className="font-display text-lg font-semibold">Guided Customer-One walkthrough</h3><p className="text-xs text-muted-foreground">{audit.walkthrough_policy}</p></div>
        <div className="grid gap-3 md:grid-cols-2">
          {audit.walkthrough.map((step) => <div key={step.key} data-testid={`walkthrough-step-${step.key}`} className={`rounded-lg border p-3 ${AUDIT_TONE[step.status] ?? ""}`}>
            <div className="flex items-start gap-3"><span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border font-mono text-xs">{step.step}</span><div className="min-w-0 flex-1"><div className="flex flex-wrap items-start justify-between gap-2"><span className="font-semibold">{step.label}</span><Badge variant="outline" className="text-[10px]">{step.status.replaceAll("_", " ")}</Badge></div><p className="mt-1 text-xs text-muted-foreground">{step.expected_evidence}</p><a href={step.action_path} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs font-semibold text-primary hover:underline">Open in new tab →</a></div></div>
          </div>)}
        </div>
      </section>
    </CardContent>
  </Card>;
}

/* ── Advisor ─────────────────────────────────────────────────────── */

function AdvisorPanel({ ws }: { ws: string }): JSX.Element {
  const q = useQuery({
    queryKey: ["intel", "briefing", ws],
    queryFn: () => getBriefing(ws),
    refetchInterval: 20_000,
  });
  const b = q.data;
  return (
    <div data-testid="advisor-panel" className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
      <Card className="border-border/60 bg-card/70">
        <CardHeader>
          <div className="flex items-center gap-2">
            <MessageSquareText className="h-4 w-4 text-indigo-500" />
            <CardTitle className="font-display text-2xl">Executive Advisor</CardTitle>
          </div>
          <CardDescription>Your morning briefing — active-voice, action-first.</CardDescription>
        </CardHeader>
        <CardContent>
          {q.isLoading ? <Skeleton className="h-40 w-full" /> : (
            <pre data-testid="advisor-briefing"
                 className="whitespace-pre-wrap break-words rounded-md border border-border/50 bg-background/60 p-4 font-mono text-sm leading-relaxed">
              {b?.briefing_text}
            </pre>
          )}
        </CardContent>
      </Card>
      <aside className="space-y-3">
        <Card className="border-border/60 bg-card/70">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Yesterday at a glance</CardTitle>
          </CardHeader>
          <CardContent>
            {q.isLoading ? <Skeleton className="h-24 w-full" /> : (
              <div className="grid grid-cols-2 gap-2 text-xs">
                <Stat label="Campaigns" value={String(b?.yesterday.campaigns_active ?? 0)} />
                <Stat label="Published Today" value={String(b?.yesterday.assets_published_today ?? 0)} />
                <Stat label="AI Cost 24h" value={cur(b?.yesterday.ai_cost_24h_usd ?? 0)} />
                <Stat label="Reach" value={b?.yesterday.estimated_reach === null ? "(pending)" : String(b?.yesterday.estimated_reach)} />
              </div>
            )}
          </CardContent>
        </Card>
        {b?.opportunity_headline ? (
          <Card data-testid="advisor-opportunity" className="border-amber-500/40 bg-amber-50/50 dark:bg-amber-950/20">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Flame className="h-4 w-4 text-amber-500" /> Opportunity
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm">{b.opportunity_headline}</CardContent>
          </Card>
        ) : null}
      </aside>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-md border border-border/50 bg-background/60 p-2">
      <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}

/* ── Opportunities + Decision Engine ─────────────────────────────── */

function OpportunitiesPanel({ ws }: { ws: string }): JSX.Element {
  const qc = useQueryClient();
  const opps = useQuery({ queryKey: ["intel", "opps", ws], queryFn: () => listOpportunities(ws), refetchInterval: 30_000 });
  const recs = useQuery({ queryKey: ["intel", "recs", ws], queryFn: () => listRecommendations(ws), refetchInterval: 30_000 });

  const accept = useMutation({
    mutationFn: (id: string) => acceptOpportunity(ws, id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["intel"] }); toast.success("Opportunity accepted"); },
  });
  const reject = useMutation({
    mutationFn: (id: string) => rejectOpportunity(ws, id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["intel"] }); toast.success("Opportunity dismissed"); },
  });
  const recAct = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "accept" | "reject" | "implement" }) =>
      recommendationAction(ws, id, action),
    onSuccess: (_, v) => { qc.invalidateQueries({ queryKey: ["intel"] }); toast.success(`Recommendation ${v.action}ed`); },
  });

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card data-testid="opportunity-list" className="border-border/60 bg-card/70">
        <CardHeader className="flex flex-col gap-3 space-y-0 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-lg">Opportunities</CardTitle>
            <CardDescription>Proactive detections — evidence-backed.</CardDescription>
          </div>
          <Badge variant="outline" className="font-mono text-[10px]">{opps.data?.length ?? 0} open</Badge>
        </CardHeader>
        <CardContent className="space-y-2">
          {opps.isLoading ? <Skeleton className="h-24 w-full" /> :
            opps.data && opps.data.length > 0 ? (opps.data as Opportunity[]).map((o) => (
              <div key={o.id} data-testid={`opportunity-row-${o.id}`}
                   className="rounded-md border border-border/50 bg-background/60 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className={`${SEVERITY_TONES[o.severity]} font-mono text-[10px] uppercase`}>{o.severity}</Badge>
                  <span className="text-sm font-semibold">{o.title}</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{o.narrative}</p>
                <p className="mt-1 text-xs text-indigo-500">→ {o.action_hint}</p>
                <div className="mt-2 flex items-center gap-1">
                  <Button size="sm" variant="outline" data-testid={`opportunity-accept-${o.id}`}
                          onClick={() => accept.mutate(o.id)}>
                    <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Accept
                  </Button>
                  <Button size="sm" variant="ghost" data-testid={`opportunity-reject-${o.id}`}
                          onClick={() => reject.mutate(o.id)}>
                    Dismiss
                  </Button>
                </div>
              </div>
            )) : <EmptyState label="No opportunities — everything looks healthy." />}
        </CardContent>
      </Card>

      <Card data-testid="recommendation-list" className="border-border/60 bg-card/70">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-lg">Decision Engine</CardTitle>
            <CardDescription>Recommendations only. No autonomous execution.</CardDescription>
          </div>
          <Badge variant="outline" className="font-mono text-[10px]">{recs.data?.length ?? 0} proposed</Badge>
        </CardHeader>
        <CardContent className="space-y-2">
          {recs.isLoading ? <Skeleton className="h-24 w-full" /> :
            recs.data && recs.data.length > 0 ? (recs.data as DecisionRecommendation[]).map((r) => (
              <div key={r.id} data-testid={`recommendation-row-${r.id}`}
                   className="rounded-md border border-border/50 bg-background/60 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className={`${PRIORITY_TONES[r.priority]} font-mono text-[10px] uppercase`}>{r.priority}</Badge>
                  <span className="text-xs font-mono text-muted-foreground uppercase tracking-widest">{r.category}</span>
                  <span className="ml-auto font-mono text-[10px] text-muted-foreground">confidence {Math.round(r.confidence * 100)}%</span>
                </div>
                <div className="mt-1 text-sm font-semibold">{r.recommendation}</div>
                <p className="mt-0.5 text-xs text-muted-foreground">Why: {r.reason}</p>
                <p className="mt-0.5 text-xs text-emerald-500">Impact: {r.potential_impact}</p>
                <div className="mt-2 flex items-center gap-1">
                  <Button size="sm" variant="outline" data-testid={`rec-accept-${r.id}`}
                          onClick={() => recAct.mutate({ id: r.id, action: "accept" })}>Accept</Button>
                  <Button size="sm" variant="outline" data-testid={`rec-implement-${r.id}`}
                          onClick={() => recAct.mutate({ id: r.id, action: "implement" })}>
                    Implement
                  </Button>
                  <Button size="sm" variant="ghost" data-testid={`rec-reject-${r.id}`}
                          onClick={() => recAct.mutate({ id: r.id, action: "reject" })}>Reject</Button>
                </div>
              </div>
            )) : <EmptyState label="No recommendations right now." />}
        </CardContent>
      </Card>
    </div>
  );
}

/* ── Performance Brain ───────────────────────────────────────────── */

function PerformancePanel({ ws }: { ws: string }): JSX.Element {
  const qc = useQueryClient();
  const [tracePackId, setTracePackId] = useState("");
  const snapshot = useQuery({ queryKey: ["intel", "performance", "snapshot", ws], queryFn: () => getPerformanceSnapshot(ws) });
  const observed = useQuery({ queryKey: ["intel", "performance", "observed", ws], queryFn: () => listPerformanceObservations(ws) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["intel", "performance"] });
  const sync = useMutation({ mutationFn: () => syncZernioPerformance(ws), onSuccess: (r: any) => {
    refresh();
    if (r.status === "provider_not_ready") toast.warning("Connect Zernio publishing before syncing evidence.");
    else toast.success(`${r.observations_created} new evidence item(s) staged`);
  }});
  const trace = useQuery({ queryKey: ["intel", "performance", "trace", ws, tracePackId], queryFn: () => getPerformanceWorkflowTrace(ws, tracePackId.trim()), enabled: Boolean(tracePackId.trim()), retry: false });
  const approve = useMutation({ mutationFn: ({ row, outcome }: { row: PerformanceObservation; outcome: "winner" | "loser" | "neutral" }) => approvePerformanceObservation(ws, row.observation_id, {
    outcome, evidence_label: `${row.target_platform ?? row.channel ?? "channel"} ${row.metric}`, sample_size: 1, confidence: 0.5,
  }), onSuccess: () => { refresh(); toast.success("Evidence approved and attached to Brain learning"); } });
  const reject = useMutation({ mutationFn: (id: string) => rejectPerformanceObservation(ws, id), onSuccess: () => { refresh(); toast.success("Evidence rejected"); } });
  const totals = snapshot.data?.approved_metric_totals ?? {};
  const stagedTotals = snapshot.data?.observed_metric_totals ?? {};
  const platforms = snapshot.data?.approved_by_platform ?? {};
  const formatEvidenceTime = (value: string | null | undefined) => value
    ? new Date(value).toLocaleString()
    : "Not yet";
  return (
    <div data-testid="performance-panel" className="space-y-4">
      <Card className="border-border/60 bg-card/70">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div><CardTitle className="text-lg">Performance Brain</CardTitle><CardDescription>Founder-approved results inform future campaign planning.</CardDescription></div>
          <Button className="w-full sm:w-auto" data-testid="performance-sync-zernio" variant="outline" onClick={() => sync.mutate()} disabled={sync.isPending}>Sync Zernio evidence</Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <section data-testid="performance-approved-results">
            <div className="mb-2 text-xs font-semibold uppercase tracking-widest text-emerald-600">Approved results</div>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
              {["impressions", "reach", "engagements", "clicks"].map((metric) => <Stat key={metric} label={metric} value={String(totals[metric] ?? 0)} />)}
            </div>
          </section>
          <section data-testid="performance-staged-results" className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
            <div className="text-xs font-semibold uppercase tracking-widest text-amber-600">Staged — not teaching the Brain</div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
              {Object.keys(stagedTotals).length === 0
                ? <span>No evidence awaiting approval.</span>
                : Object.entries(stagedTotals).map(([metric, value]) => <span key={metric} className="capitalize">{metric.replace(/_/g, " ")}: {value}</span>)}
            </div>
          </section>
          <div className="grid gap-2 text-xs text-muted-foreground md:grid-cols-3">
            <div><span className="font-semibold text-foreground">Evidence sources:</span> {snapshot.data?.current_sources.join(", ") || "None"}</div>
            <div><span className="font-semibold text-foreground">Last evidence:</span> {formatEvidenceTime(snapshot.data?.last_observed_at)}</div>
            <div><span className="font-semibold text-foreground">Last approval:</span> {formatEvidenceTime(snapshot.data?.last_approved_at)}</div>
          </div>
          <p className="text-xs text-muted-foreground">Founder approval is required before evidence teaches the Brain.</p>
        </CardContent>
      </Card>
      <Card data-testid="performance-workflow-truth" className="border-border/60 bg-card/70">
        <CardHeader><CardTitle className="text-lg">Customer workflow truth</CardTitle><CardDescription>Trace one campaign pack across generation, review, Vault, publishing, metrics, and Brain learning.</CardDescription></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row"><Input aria-label="Campaign pack ID" placeholder="cbp_…" value={tracePackId} onChange={(e) => setTracePackId(e.target.value)} /><Button className="w-full sm:w-auto" variant="outline" onClick={() => trace.refetch()} disabled={!tracePackId.trim()}>Trace</Button></div>
          {trace.isError && <p className="text-sm text-destructive">Campaign pack not found in this workspace.</p>}
          {trace.data && <WorkflowTruth trace={trace.data} />}
        </CardContent>
      </Card>
      <Card className="border-border/60 bg-card/70">
        <CardHeader><CardTitle className="text-lg">Approved results by platform</CardTitle><CardDescription>Only founder-approved evidence is included.</CardDescription></CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2">
          {Object.keys(platforms).length === 0 ? <EmptyState label="No approved platform evidence yet." /> : Object.entries(platforms).map(([platform, metrics]) => (
            <div key={platform} data-testid={`performance-platform-${platform}`} className="rounded-md border border-border/50 bg-background/60 p-3">
              <div className="mb-2 font-semibold capitalize">{platform.replace(/_/g, " ")}</div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                {Object.entries(metrics).map(([metric, value]) => <span key={metric} className="capitalize">{metric.replace(/_/g, " ")}: {value}</span>)}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
      <Card className="border-border/60 bg-card/70">
        <CardHeader><CardTitle className="text-lg">Evidence awaiting approval</CardTitle><CardDescription>Cumulative provider snapshots; newer values replace older approved values only after approval.</CardDescription></CardHeader>
        <CardContent className="space-y-2">
          {observed.isLoading ? <Skeleton className="h-24 w-full" /> : (observed.data?.length ?? 0) === 0 ? <EmptyState label="No performance evidence is waiting." /> : observed.data!.map((row: PerformanceObservation) => (
            <div key={row.observation_id} data-testid={`performance-observation-${row.observation_id}`} className="flex flex-wrap items-center gap-3 rounded-md border border-border/50 bg-background/60 p-3">
              <div className="min-w-0 flex-1"><div className="font-semibold capitalize">{row.metric.replace(/_/g, " ")}: {row.value}</div><div className="text-xs text-muted-foreground">{row.target_platform ?? row.channel ?? "Unknown channel"} · {row.permanent_asset_id ?? row.external_publication_id ?? "Publication"}</div></div>
              {row.campaign_pack_id && <Button size="sm" variant="outline" onClick={() => setTracePackId(row.campaign_pack_id!)}>Trace</Button>}
              <Button size="sm" onClick={() => approve.mutate({ row, outcome: "winner" })}>Approve winner</Button>
              <Button size="sm" variant="outline" onClick={() => approve.mutate({ row, outcome: "neutral" })}>Approve neutral</Button>
              <Button size="sm" variant="ghost" onClick={() => reject.mutate(row.observation_id)}>Reject</Button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function WorkflowTruth({ trace }: { trace: WorkflowTrace }): JSX.Element {
  return <div className="space-y-2"><div className="font-semibold">{trace.campaign_name}</div><div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">{trace.stages.map((stage) => <div key={stage.name} data-testid={`workflow-stage-${stage.name}`} className="rounded-md border border-border/50 p-3"><div className="flex items-center justify-between"><span className="font-semibold capitalize">{stage.name}</span><Badge variant="outline">{stage.state.replace("_", " ")}</Badge></div><p className="mt-1 text-xs text-muted-foreground">{stage.detail}</p>{stage.blocker && <p className="mt-1 text-xs text-amber-600">{stage.blocker}</p>}</div>)}</div><p className="text-sm"><span className="font-semibold">Next:</span> {trace.next_action}</p></div>;
}

/* ── Spend ───────────────────────────────────────────────────────── */

function SpendPanel({ ws }: { ws: string }): JSX.Element {
  const q = useQuery({ queryKey: ["intel", "spend", ws], queryFn: () => getSpendRollup(ws), refetchInterval: 30_000 });
  const b = useQuery({ queryKey: ["intel", "budget", ws], queryFn: () => getBudget(ws), refetchInterval: 30_000 });
  const r = q.data; const bud = b.data;
  return (
    <div data-testid="spend-panel" className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <KpiCard testid="spend-24h" icon={<Coins className="h-4 w-4" />} label="24 h AI Spend" value={r ? cur(r.day_24h.cost) : "…"} hint={`${r?.day_24h.calls ?? 0} calls`} />
      <KpiCard testid="spend-7d" icon={<TrendingUp className="h-4 w-4" />} label="7 d AI Spend" value={r ? cur(r.day_7d.cost) : "…"} hint={`${r?.day_7d.calls ?? 0} calls`} />
      <KpiCard testid="spend-30d" icon={<LineChart className="h-4 w-4" />} label="30 d AI Spend" value={r ? cur(r.day_30d.cost) : "…"} hint={`${r?.day_30d.calls ?? 0} calls`} />
      <KpiCard testid="spend-waste" icon={<TrendingDown className="h-4 w-4" />} label="Waste Ratio" value={r ? `${r.waste_ratio_pct}%` : "…"} hint={`${r?.stalled_heads ?? 0} stalled drafts`} />
      <Card className="col-span-full border-border/60 bg-card/70">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Top-cost model / prompt combinations (30 d)</CardTitle>
        </CardHeader>
        <CardContent>
          {q.isLoading ? <Skeleton className="h-32 w-full" /> :
            (r?.by_model.length ?? 0) === 0 ? <EmptyState label="No AI spend yet." /> : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    <th className="pb-2">Provider</th><th className="pb-2">Model</th><th className="pb-2">Content Type</th><th className="pb-2 text-right">Cost</th><th className="pb-2 text-right">Calls</th>
                  </tr>
                </thead>
                <tbody>
                  {r!.by_model.slice(0, 10).map((s, i) => (
                    <tr key={i} className="border-t border-border/40">
                      <td className="py-1.5">{s.provider ?? "—"}</td>
                      <td className="py-1.5 font-mono text-xs">{s.model ?? "—"}</td>
                      <td className="py-1.5 font-mono text-xs">{s.content_type ?? "—"}</td>
                      <td className="py-1.5 text-right tabular-nums">{cur(s.cost_usd)}</td>
                      <td className="py-1.5 text-right tabular-nums">{s.calls}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </CardContent>
      </Card>
      {bud ? (
        <Card data-testid="spend-budget-summary" className="col-span-full border-border/60 bg-card/70">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Budget status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <div><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Status</div>
                <div className={`mt-1 text-xl ${BUDGET_STATUS_TONES[bud.status]}`}>{bud.status.replace(/_/g, " ")}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Today</div>
                <div className="mt-1 text-xl">{cur(bud.spend_today_usd)}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-muted-foreground">This Month</div>
                <div className="mt-1 text-xl">{cur(bud.spend_month_usd)}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Est. Month-End</div>
                <div className="mt-1 text-xl">{cur(bud.estimated_month_end_usd)}</div></div>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

/* ── Brand ───────────────────────────────────────────────────────── */

function BrandPanel({ ws }: { ws: string }): JSX.Element {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["intel", "brand", ws], queryFn: () => getBrand(ws), refetchInterval: 30_000 });
  const rescore = useMutation({
    mutationFn: () => rescoreBrand(ws),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["intel"] }); toast.success(`Rescored ${r.rescored} assets`); },
  });
  return (
    <div data-testid="brand-panel" className="grid gap-4 md:grid-cols-3">
      <Card className="border-border/60 bg-card/70">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-500" />
            <CardTitle className="text-base">Voice Consistency</CardTitle>
          </div>
          <CardDescription>Brand Brain (60%) + top approved assets (40%)</CardDescription>
        </CardHeader>
        <CardContent>
          {q.isLoading ? <Skeleton className="h-24 w-full" /> : (
            <div>
              <div className="font-display text-5xl font-semibold tabular-nums text-emerald-500">
                {q.data?.summary.score ?? "—"}
                <span className="ml-1 text-base font-normal text-muted-foreground">/100</span>
              </div>
              <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {q.data?.summary.on_brand ?? 0} on-brand · {q.data?.summary.off_brand ?? 0} off-brand ·
                {" "}{q.data?.summary.sample_size ?? 0} assets scored
              </div>
              <Button data-testid="brand-rescore" size="sm" variant="outline" className="mt-3"
                      onClick={() => rescore.mutate()} disabled={rescore.isPending}>
                <Wand2 className="mr-1 h-3.5 w-3.5" /> Rescore workspace
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      <Card className="md:col-span-2 border-border/60 bg-card/70">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Lowest-scoring assets</CardTitle>
          <CardDescription>Review these first — the fastest path back on-brand.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {q.isLoading ? <Skeleton className="h-24 w-full" /> :
            (q.data?.lowest_scoring.length ?? 0) === 0 ? <EmptyState label="No scored assets yet. Rescore to populate." /> : (
              q.data!.lowest_scoring.slice(0, 6).map((s) => (
                <div key={s._id} data-testid={`brand-low-${s._id}`} className="rounded-md border border-border/50 bg-background/60 p-3 text-sm">
                  <div className="flex items-center justify-between">
                    <Badge className={`font-mono text-[10px] uppercase ${s.verdict === "off_brand" ? "bg-rose-100 text-rose-900 dark:bg-rose-950/60 dark:text-rose-200" : s.verdict === "on_brand" ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-950/60 dark:text-emerald-200" : "bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-200"}`}>{s.verdict}</Badge>
                    <span className="text-xl tabular-nums">{s.score}</span>
                  </div>
                  <div className="mt-1 truncate text-xs text-muted-foreground">{s.permanent_asset_id ?? s.asset_id}</div>
                  {s.hints?.length ? (
                    <ul className="mt-1 list-disc space-y-0.5 pl-4 text-[11px] text-muted-foreground">
                      {s.hints.slice(0, 2).map((h, i) => <li key={i}>{h}</li>)}
                    </ul>
                  ) : null}
                </div>
              ))
            )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ── Campaigns ───────────────────────────────────────────────────── */

function CampaignsPanel({ ws }: { ws: string }): JSX.Element {
  const q = useQuery({ queryKey: ["intel", "campaigns", ws], queryFn: () => getStalledCampaigns(ws), refetchInterval: 30_000 });
  return (
    <Card data-testid="campaigns-panel" className="border-border/60 bg-card/70">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Megaphone className="h-4 w-4 text-indigo-500" />
          <CardTitle className="text-lg">Campaigns needing attention</CardTitle>
        </div>
        <CardDescription>Sorted by bottleneck gap × completion.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {q.isLoading ? <Skeleton className="h-32 w-full" /> :
          (q.data?.length ?? 0) === 0 ? <EmptyState label="No campaigns stalled — pipeline flowing." /> :
          q.data!.map((c) => (
            <div key={c.campaign_pack_id} data-testid={`campaign-row-${c.campaign_pack_id}`}
                 className="rounded-md border border-border/50 bg-background/60 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="font-semibold">{c.campaign_name}</div>
                  <div className="mt-0.5 font-mono text-[11px] text-muted-foreground">
                    {c.delivered}/{c.planned_assets} delivered · {c.completion_pct ?? 0}% complete
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <Badge variant="outline" className="font-mono text-[10px] uppercase">
                    bottleneck: {c.bottleneck_stage ?? "none"}
                  </Badge>
                  <Badge variant="outline" className="font-mono text-[10px]">gap {c.bottleneck_gap}</Badge>
                  <Badge variant="outline" className="font-mono text-[10px]">fail {c.fail_rate_pct}%</Badge>
                </div>
              </div>
            </div>
          ))}
      </CardContent>
    </Card>
  );
}

/* ── Reports ─────────────────────────────────────────────────────── */

function ReportsPanel({ ws }: { ws: string }): JSX.Element {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["intel", "reports", ws], queryFn: () => listReports(ws), refetchInterval: 60_000 });
  const gen = useMutation({
    mutationFn: (cadence: ReportCadence) => generateReport(ws, cadence),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["intel", "reports", ws] }); toast.success("Report generated"); },
    onError: (e: Error) => toast.error(e.message),
  });
  const CADENCES: { key: ReportCadence; label: string; icon: JSX.Element }[] = [
    { key: "daily_pulse", label: "Daily Pulse", icon: <Gauge className="h-3.5 w-3.5" /> },
    { key: "weekly_digest", label: "Weekly Digest", icon: <FileText className="h-3.5 w-3.5" /> },
    { key: "monthly_review", label: "Monthly Review", icon: <LineChart className="h-3.5 w-3.5" /> },
    { key: "campaign_post_mortem", label: "Campaign Post-Mortem", icon: <Target className="h-3.5 w-3.5" /> },
  ];
  return (
    <Card data-testid="reports-panel" className="border-border/60 bg-card/70">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-lg">Executive Reports</CardTitle>
          <CardDescription>Daily · Weekly · Monthly · Post-Mortem</CardDescription>
        </div>
        <div className="flex flex-wrap gap-1">
          {CADENCES.map((c) => (
            <Button key={c.key} size="sm" variant="outline" data-testid={`report-gen-${c.key}`}
                    disabled={gen.isPending} onClick={() => gen.mutate(c.key)}>
              {c.icon}<span className="ml-1">{c.label}</span>
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {q.isLoading ? <Skeleton className="h-24 w-full" /> :
          (q.data?.length ?? 0) === 0 ? <EmptyState label="No reports yet. Generate a Daily Pulse to get started." /> :
          (q.data as ExecReport[]).map((r) => (
            <div key={r.id} data-testid={`report-row-${r.id}`}
                 className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border/50 bg-background/60 p-3 text-sm">
              <div>
                <div className="font-semibold">{r.title}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  {r.cadence.replace(/_/g, " ")} · {new Date(r.created_at).toLocaleString()}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a
                  className="inline-flex items-center gap-1 rounded-md border border-border/60 px-2 py-1 text-xs hover:bg-accent"
                  data-testid={`report-open-${r.id}`}
                  target="_blank" rel="noreferrer"
                  href={`${import.meta.env.VITE_BACKEND_URL ?? ""}/api/v1/workspaces/${ws}/intelligence/reports/${r.id}/html`}
                >
                  Open <ChevronRight className="h-3 w-3" />
                </a>
              </div>
            </div>
          ))}
      </CardContent>
    </Card>
  );
}

/* ── Workforce ───────────────────────────────────────────────────── */

function WorkforcePanel({ ws }: { ws: string }): JSX.Element {
  const q = useQuery({ queryKey: ["intel", "workforce", ws], queryFn: () => getWorkforce(ws), refetchInterval: 20_000 });
  return (
    <Card data-testid="workforce-panel" className="border-border/60 bg-card/70">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Bot className="h-4 w-4 text-fuchsia-500" />
          <CardTitle className="text-lg">AI Workforce</CardTitle>
        </div>
        <CardDescription>Dry-run only in M9. Every agent shows what it *would* do.</CardDescription>
      </CardHeader>
      <CardContent>
        {q.isLoading ? <Skeleton className="h-40 w-full" /> :
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {q.data?.agents.map((a) => {
              const plan = q.data?.plans.find((p) => p.agent_kind === a.kind);
              return (
                <div key={a.id} data-testid={`agent-${a.kind}`}
                     className="rounded-md border border-border/50 bg-background/60 p-3">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold">{a.display_name}</div>
                    <Badge variant="outline" className="font-mono text-[10px]">{Math.round(a.utilisation_pct)}%</Badge>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{a.mission}</p>
                  {plan ? (
                    <div className="mt-2 rounded-md border border-dashed border-border/50 bg-background/40 p-2 text-xs">
                      <div className="font-mono text-[9px] uppercase tracking-widest text-fuchsia-500">Proposed action</div>
                      <div className="mt-0.5">{plan.proposed_action}</div>
                      <div className="mt-0.5 text-[10px] text-muted-foreground">{plan.rationale}</div>
                    </div>
                  ) : null}
                  <div className="mt-2 flex items-center gap-1 text-[10px] text-muted-foreground">
                    <PauseCircle className="h-3 w-3" /> Dry-run · autonomy off
                  </div>
                </div>
              );
            })}
          </div>}
      </CardContent>
    </Card>
  );
}

/* ── Budget ──────────────────────────────────────────────────────── */

function BudgetPanel({ ws }: { ws: string }): JSX.Element {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["intel", "budget", ws], queryFn: () => getBudget(ws), refetchInterval: 30_000 });
  const [draft, setDraft] = useState<BudgetThreshold | null>(null);
  const t = draft ?? q.data?.threshold ?? null;

  const save = useMutation({
    mutationFn: (payload: BudgetThreshold) => putBudget(ws, payload),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["intel", "budget", ws] }); setDraft(null); toast.success("Budget updated"); },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card data-testid="budget-panel" className="border-border/60 bg-card/70">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Wallet className="h-4 w-4 text-emerald-500" />
          <CardTitle className="text-lg">AI Budget Envelopes</CardTitle>
        </div>
        <CardDescription>Soft warning · Hard stop · Emergency override</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {q.isLoading || !t ? <Skeleton className="h-32 w-full" /> : (
          <>
            <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-6">
              <BudgetField label="Daily USD" value={t.daily_usd}
                           onChange={(v) => setDraft({ ...t, daily_usd: v })} testId="budget-daily" />
              <BudgetField label="Monthly USD" value={t.monthly_usd}
                           onChange={(v) => setDraft({ ...t, monthly_usd: v })} testId="budget-monthly" />
              <BudgetField label="Warning %" value={Math.round(t.warning_pct * 100 * 100) / 100}
                           onChange={(v) => setDraft({ ...t, warning_pct: v / 100 })} testId="budget-warning" />
              <BudgetField label="Hard Stop %" value={Math.round(t.hard_stop_pct * 100 * 100) / 100}
                           onChange={(v) => setDraft({ ...t, hard_stop_pct: v / 100 })} testId="budget-hard-stop" />
              <div className="flex items-center gap-2 rounded-md border border-border/50 bg-background/60 p-2">
                <Switch data-testid="budget-emergency" checked={t.emergency_override}
                        onCheckedChange={(v) => setDraft({ ...t, emergency_override: v })} />
                <Label className="text-xs">Emergency Override</Label>
              </div>
              <Button data-testid="budget-save"
                      disabled={!draft || save.isPending}
                      onClick={() => draft && save.mutate(draft)}>
                Save
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <Stat label="Today" value={cur(q.data!.spend_today_usd)} />
              <Stat label="This Month" value={cur(q.data!.spend_month_usd)} />
              <Stat label="Est. Month-End" value={cur(q.data!.estimated_month_end_usd)} />
              <Stat label="Status" value={q.data!.status.replace(/_/g, " ")} />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function BudgetField({
  label, value, onChange, testId,
}: { label: string; value: number; onChange: (v: number) => void; testId: string }): JSX.Element {
  return (
    <div className="space-y-1">
      <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</Label>
      <Input data-testid={testId} type="number" step="0.01" value={value}
             onChange={(e) => onChange(Number(e.target.value) || 0)} />
    </div>
  );
}

/* ── Competitive ─────────────────────────────────────────────────── */

function CompetitivePanel({ ws }: { ws: string }): JSX.Element {
  const q = useQuery({ queryKey: ["intel", "competitive", ws], queryFn: () => listCompetitiveProviders(ws) });
  return (
    <Card data-testid="competitive-panel" className="border-border/60 bg-card/70">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Compass className="h-4 w-4 text-teal-500" />
          <CardTitle className="text-lg">Competitive Intelligence</CardTitle>
        </div>
        <CardDescription>Interfaces reserved. No live ingestion in M9.</CardDescription>
      </CardHeader>
      <CardContent>
        {q.isLoading ? <Skeleton className="h-16 w-full" /> :
          <>
            <div className="mb-3 rounded-md border border-dashed border-border/60 bg-background/40 p-3 text-xs text-muted-foreground">
              {q.data?.note}
            </div>
            <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
              {q.data?.interfaces?.map((iface: string) => (
                <div key={iface} data-testid={`interface-${iface}`}
                     className="rounded-md border border-border/50 bg-background/60 p-3 text-sm">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-teal-500">Interface</div>
                  <div className="mt-0.5 font-semibold">{iface}</div>
                  <div className="mt-0.5 text-[10px] text-muted-foreground">Awaiting adapter registration.</div>
                </div>
              ))}
            </div>
          </>}
      </CardContent>
    </Card>
  );
}

/* ── shared ─────────────────────────────────────────────────────── */

function KpiCard({
  testid, icon, label, value, hint,
}: { testid: string; icon: JSX.Element; label: string; value: string; hint?: string }): JSX.Element {
  return (
    <Card data-testid={testid} className="border-border/60 bg-card/70">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {icon}<span className="font-mono uppercase tracking-widest">{label}</span>
        </div>
        <div className="mt-2 font-display text-3xl font-semibold tabular-nums">{value}</div>
        {hint ? <div className="mt-1 text-[10px] text-muted-foreground">{hint}</div> : null}
      </CardContent>
    </Card>
  );
}

function EmptyState({ label }: { label: string }): JSX.Element {
  return (
    <div className="rounded-md border border-dashed border-border/60 px-3 py-8 text-center text-xs text-muted-foreground">
      {label}
    </div>
  );
}
